// src/pages/Contact.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import AnimatedSocialLinks from '../components/AnimatedSocialLinks';
import './Home.css'; // Re-use Home's beautiful styles

const API_URL = process.env.REACT_APP_API_URL;

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('');

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/contact/`, formData, { withCredentials: true });
      setStatus('Message sent successfully!');
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setStatus('Message sending failed. Please try again.');
      toast.error('Message sending failed. Please try again.');
      console.error('Contact failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      {/* ───── ANIMATED BACKGROUND BLOBS ───── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* ───── SEO ───── */}
      <Helmet>
        <title>Contact SErraND - Trusted Service Providers in Kenya</title>
        <meta
          name="description"
          content="Get in touch with SErraND to connect with reliable service providers in Kenya. Send us a message or donate to support our mission to revolutionize the service industry."
        />
        <meta name="keywords" content="SErraND, contact, service providers, Kenya, home services, reliable professionals, plug wa kazi, mtu wa kazi, kazi, serrand, provider" />
        <meta name="author" content="Marvin Useki" />
        <link rel="canonical" href="https://www.serrand.org/contact/" />
        <meta property="og:title" content="Contact SErraND - Trusted Service Providers in Kenya" />
        <meta property="og:description" content="Reach out to SErraND to find trusted service providers or support our mission to connect Kenya with quality services." />
        <meta property="og:url" content="https://www.serrand.org/contact/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.serrand.org/images/contact-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'SErraND',
            url: 'https://www.serrand.org',
            logo: 'https://www.serrand.org/logo512.png',
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+254-740-879229',
              contactType: 'Customer Service',
              areaServed: 'KE',
              availableLanguage: 'English',
            },
            description: 'SErraND connects clients with trusted service providers across Kenya.',
          })}
        </script>
      </Helmet>

      {/* ───── NAVIGATION (Home.js style, no auth) ───── */}
      <nav className="relative z-40 bg-gray-900 bg-opacity-80 backdrop-blur-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                <span className="text-2xl font-bold">SE</span>
                <span className="text-2xl font-bold">RRA</span>
                <span className="text-2xl font-bold">ND</span>
              </div>
            </Link>

            <div className="hidden md:flex md:items-center md:space-x-6">
              <Link to="/" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Home</Link>
              <Link to="/profile" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Profile</Link>
              <Link to="/contact" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Contact</Link>
            </div>

            <button
              className="md:hidden relative w-8 h-8 flex flex-col justify-center items-center"
              onClick={() => document.getElementById('mobile-menu')?.classList.toggle('hidden')}
              aria-label="Toggle menu"
            >
              <span className="w-6 h-0.5 bg-white transition-all duration-300"></span>
              <span className="w-6 h-0.5 bg-white transition-all duration-300 mt-1"></span>
              <span className="w-6 h-0.5 bg-white transition-all duration-300 mt-1"></span>
            </button>
          </div>
        </div>

        <div id="mobile-menu" className="md:hidden hidden bg-gray-900 bg-opacity-95 backdrop-blur-lg border-t border-gray-700">
          <div className="px-4 py-4 space-y-2">
            <Link to="/" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Home</Link>
            <Link to="/profile" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Profile</Link>
            <Link to="/contact" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Contact</Link>
          </div>
        </div>
      </nav>

      {/* ───── ABOUT SECTION - YOUR EXACT WORDS ───── */}
      <section className="relative py-20 px-4 max-w-6xl mx-auto z-10">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">About SErraND</h1>
          <p className="text-xl text-gray-300">Connecting Kenya, One Service at a Time</p>
        </div>

        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-8 md:p-12 shadow-2xl backdrop-blur-lg max-w-5xl mx-auto">
          <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
            <p>
              SErraND was born from a spark of necessity and a vision for change. I’m <strong>Marvin Useki</strong>, a 25-year-old innovator based in Kitengela, Kajiado County, Kenya. When I moved to Kitengela, I faced a challenge many can relate to: finding reliable service providers. From plumbers to tutors, the search was daunting, especially as a newcomer with no local connections.
            </p>
            <p>
              That frustration turned into inspiration. I saw an opportunity to solve a real problem—not just for myself, but for countless others across Kenya. SErraND is the result of countless late nights, fueled by chai and determination, coding and debugging to create a platform that connects clients with trusted service providers seamlessly.
            </p>
            <p>
              As the sole developer behind SErraND, my mission is to revolutionize Kenya’s service industry by making it easier, safer, and more transparent to access services. Every line of code is a step toward empowering communities and fostering trust. But this journey is just beginning, and I’m inviting you to join me.
            </p>
            <p>
              Support SErraND’s growth with a donation to help us become the go-to platform for services in Kenya. Together, let’s build a future where finding the right help is just a click away!
            </p>

            <div className="mt-8 p-6 bg-gradient-to-r from-blue-900 to-purple-900 rounded-xl border border-blue-700">
              <h3 className="text-2xl font-bold text-white mb-4">Support SErraND</h3>
              <p className="text-white">
                <strong>Donate via M-Pesa:</strong>{' '}
                <a href="tel:+254740879229" className="text-blue-300 hover:underline">0740 879 229</a>
              </p>
              <p className="text-white mt-2">
                <strong>Donate via Bank:</strong> Paybill Number: 625625, Account Number: 20138250
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── CONTACT FORM - LAST, AS REQUESTED ───── */}
      <section className="relative py-16 px-4 max-w-6xl mx-auto z-10">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-xl text-gray-300">We’d love to hear from you!</p>
        </div>

        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-8 md:p-12 shadow-2xl backdrop-blur-lg max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            <input
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Your Name"
              required
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            />
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Your Email"
              required
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            />
            <input
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              placeholder="Subject"
              required
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            />
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Your Message"
              rows={5}
              required
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
            />
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-green-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-green-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-green-500 transform hover:scale-105 transition-all"
            >
              Send Message
            </button>
            {status && (
              <p className={`text-center mt-4 ${status.includes('failed') ? 'text-red-400' : 'text-green-400'}`}>
                {status}
              </p>
            )}
          </form>
        </div>
      </section>

      {/* ───── FOOTER (Home.js style) ───── */}
      <footer className="bg-gray-900 bg-opacity-90 backdrop-blur-lg border-t border-gray-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-4 group">
                <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                  <span className="text-3xl font-bold">SE</span>
                  <span className="text-3xl font-bold">RRA</span>
                  <span className="text-3xl font-bold">ND</span>
                </div>
              </Link>
              <p className="text-gray-300 text-lg mb-4 leading-relaxed">
                Your trusted platform for connecting with local service providers across Kenya.
              </p>
              <AnimatedSocialLinks />
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-300 hover:text-white transition-colors hover:underline">Home</Link></li>
                <li><Link to="/profile" className="text-gray-300 hover:text-white transition-colors hover:underline">Profile</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors hover:underline">Contact</Link></li>
                <li><Link to="/search" className="text-gray-300 hover:text-white transition-colors hover:underline">Search Providers</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-4">Legal & Support</h3>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-gray-300 hover:text-white transition-colors hover:underline">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white transition-colors hover:underline">Privacy Policy</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors hover:underline">About Us</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 mt-8 text-center">
            <p className="text-gray-400">© 2025 SErraND. All rights reserved. Made with love in Kenya</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
